import React, { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import SeoHead from '@components/SeoHead';
import GameDetailView from '@components/GameDetailView';
import { getGameBySlug } from '@lib/content';
import { buildGameSchema, buildGameBreadcrumbSchema, buildFAQSchema, combineSchemas } from '@lib/jsonld';
import categories from '@content/categories.json';
import { siteConfig } from '../config';
import { useGameRecommendations } from '@hooks/useGameRecommendations';
import { useGamePlayer } from '@hooks/useGamePlayer';

const GamePage: React.FC = () => {
  const { slug } = useParams();
  const game = slug ? getGameBySlug(slug) : null;
  
  const {
    iframeRef,
    gamePlayerRef,
    showBackToGame,
    gameStarted,
    isLoading,
    isTheaterMode,
    handlePlayClick,
    handleIframeLoad,
    scrollToGame,
    toggleTheaterMode,
  } = useGamePlayer({ gameSlug: slug });

  const { sidebarGames, moreGames } = useGameRecommendations(game ?? null);

  if (!slug || !game) {
    return <p className="muted">Game not found.</p>;
  }

  // Get primary category for breadcrumb (first in categories array)
  const primaryCategory = game.categories?.[0];
  const currentCategory = primaryCategory ? categories.find(c => c.slug === primaryCategory) : null;

  const jsonLdSchemas = useMemo(() => {
    const schemas: any[] = [
      buildGameSchema(game),
      buildGameBreadcrumbSchema(game, currentCategory?.name, currentCategory?.slug)
    ];
    
    // Add FAQ schema if game has FAQs
    if (game.faqs && game.faqs.length > 0) {
      const faqSchema = buildFAQSchema(game.faqs);
      if (faqSchema) schemas.push(faqSchema);
    }
    
    return combineSchemas(...schemas);
  }, [game, currentCategory]);

  return (
    <>
      <SeoHead
        title={game.title}
        description={game.description}
        image={game.thumbnail}
        jsonLd={jsonLdSchemas}
      />
      <GameDetailView
        game={game}
        slug={slug}
        gameStarted={gameStarted}
        isLoading={isLoading}
        showBackToGame={showBackToGame}
        sidebarGames={sidebarGames}
        moreGames={moreGames}
        iframeRef={iframeRef}
        gamePlayerRef={gamePlayerRef}
        onPlayClick={handlePlayClick}
        onIframeLoad={handleIframeLoad}
        onScrollToGame={scrollToGame}
        isTheaterMode={isTheaterMode}
        onTheaterModeToggle={toggleTheaterMode}
      />
    </>
  );
};

export default GamePage;
